import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import '../styles/liveDemo.css';
import { initialContent, defaultSections, surfaceColor } from '../data/liveDemoDefaults';
import { db } from '../firebase';

const sectionLabels = {
    adBanner: 'Ad banner',
    nav: 'Navigation',
    hero: 'Hero',
    icons: 'Icon section',
    vehicles: 'Fleet list',
    testimonials: 'Testimonials',
    why: 'Why rent with us',
    photos: 'Photos',
    email: 'Email subscription',
    footer: 'Footer',
};

const fallbackTestimonials = [
    {
        name: 'Jordan Wells',
        title: 'Business traveler · Miami Airport',
        quote: 'Pickup took under 5 minutes and the SUV was spotless. Felt like a premium rental without the premium price.',
        image: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=240&q=80',
    },
    {
        name: 'Priya Desai',
        title: 'Family vacation · Orlando',
        quote: 'We landed at midnight and still checked out with zero lines. Support texted me the lockbox code before we landed.',
        image: 'https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=crop&w=240&q=80',
    },
    {
        name: 'Evan Brooks',
        title: 'Repeat renter · Tampa',
        quote: 'Extended my rental from my phone and kept the same rate. Fleet photos matched exactly what I picked up.',
        image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=240&q=80',
    },
];

const fallbackWhyIcons = ['🚗', '⚡', '🛡️'];
const themePresets = [
    {
        name: 'Midnight',
        accent: '#7c3aed',
        background: '#0b1020',
        palette: {
            nav: '#6366f1',
            hero: '#8b5cf6',
            heroSecondary: '#a78bfa',
            icons: '#a78bfa',
            vehicles: '#60a5fa',
            testimonials: '#22d3ee',
            why: '#38bdf8',
            photos: '#818cf8',
            email: '#f472b6',
            adBanner: '#34d399',
            footer: '#94a3b8',
        },
        surfaces: {
            nav: '#23284a',
            icons: '#262d52',
            why: '#242c4d',
            email: '#273150',
            adBanner: '#1f2c44',
        },
    },
    {
        name: 'Ocean',
        accent: '#0ea5e9',
        background: '#0b1220',
        palette: {
            nav: '#06b6d4',
            hero: '#38bdf8',
            heroSecondary: '#7dd3fc',
            icons: '#22d3ee',
            vehicles: '#0ea5e9',
            testimonials: '#14b8a6',
            why: '#2dd4bf',
            photos: '#38bdf8',
            email: '#60a5fa',
            adBanner: '#34d399',
            footer: '#7c9cff',
        },
        surfaces: {
            nav: '#133149',
            icons: '#14344d',
            why: '#143752',
            email: '#15344b',
            adBanner: '#143246',
        },
    },
    {
        name: 'Cinder',
        accent: '#f97316',
        background: '#1b0f12',
        palette: {
            nav: '#f97316',
            hero: '#fb7185',
            heroSecondary: '#fda4af',
            icons: '#f59e0b',
            vehicles: '#f97316',
            testimonials: '#fb923c',
            why: '#facc15',
            photos: '#fda4af',
            email: '#f87171',
            adBanner: '#f59e0b',
            footer: '#f5b98d',
        },
        surfaces: {
            nav: '#352125',
            icons: '#3a2429',
            why: '#382327',
            email: '#372226',
            adBanner: '#341f23',
        },
    },
    {
        name: 'Evergreen',
        accent: '#22c55e',
        background: '#0b1712',
        palette: {
            nav: '#16a34a',
            hero: '#22c55e',
            heroSecondary: '#4ade80',
            icons: '#34d399',
            vehicles: '#2dd4bf',
            testimonials: '#14b8a6',
            why: '#22c55e',
            photos: '#4ade80',
            email: '#86efac',
            adBanner: '#10b981',
            footer: '#9ca3af',
        },
        surfaces: {
            nav: '#1a3124',
            icons: '#1b3527',
            why: '#1b3427',
            email: '#1c3226',
            adBanner: '#193023',
        },
    },
];

function LiveDemo() {
    const iconChoices = ['📱', '💸', '⭐', '⏱️', '🧭', '🚗', '🔒', '✨'];
    const navigate = useNavigate();
    const [sections, setSections] = useState(defaultSections);
    const [navOpen, setNavOpen] = useState(false);
    const maxLogoSizeBytes = 2 * 1024 * 1024;
    const maxVehicleImageBytes = 3 * 1024 * 1024;
    const [content, setContent] = useState(() => {
        const baseContent = {
            ...initialContent,
            testimonials: {
                ...initialContent.testimonials,
                items: (initialContent.testimonials.items && initialContent.testimonials.items.length
                    ? initialContent.testimonials.items
                    : fallbackTestimonials
                ).map((item) => ({ ...item })),
            },
        };
        const navAccent = baseContent.nav?.accent || '#6366f1';
        const navBackgroundMode = baseContent.nav?.backgroundMode || 'gradient';
        const navBackgroundColor = baseContent.nav?.backgroundColor || surfaceColor;
        const navGradientColor = baseContent.nav?.gradientColor || surfaceColor;
        const linkColors = Array.isArray(baseContent.nav?.linkColors) && baseContent.nav.linkColors.length
            ? baseContent.nav.linkColors
            : (baseContent.nav?.links || []).map(() => navAccent);
        const heroAccent = baseContent.hero?.accent || '#7c3aed';
        const heroBackgroundMediaType = baseContent.hero?.backgroundMediaType || 'video';
        const heroBackgroundMediaUrl = baseContent.hero?.backgroundMediaUrl
            || 'https://www.fiveguycarrentals.com/theme/img/videoplayback.mp4';
        const iconsAccent = baseContent.icons?.accent || '#7c3aed';
        const iconsBackgroundMode = baseContent.icons?.backgroundMode || 'gradient';
        const iconsBackgroundColor = baseContent.icons?.backgroundColor || surfaceColor;
        const iconsGradientColor = baseContent.icons?.gradientColor || surfaceColor;
        const iconsTextColor = baseContent.icons?.textColor || '#e2e8f0';
        const iconsIconColor = baseContent.icons?.iconColor || iconsAccent;
        const adBannerAccent = baseContent.adBanner?.accent || heroAccent;
        const adBannerBackgroundMode = baseContent.adBanner?.backgroundMode || 'gradient';
        const adBannerBackgroundColor = baseContent.adBanner?.backgroundColor || surfaceColor;
        const adBannerGradientColor = baseContent.adBanner?.gradientColor || surfaceColor;
        const adBannerTextColor = baseContent.adBanner?.textColor || '#e2e8f0';
        const vehiclesBackgroundMode = baseContent.vehicles?.backgroundMode || 'gradient';
        const vehiclesBackgroundColor = baseContent.vehicles?.backgroundColor || surfaceColor;
        const vehiclesGradientColor = baseContent.vehicles?.gradientColor || surfaceColor;
        const vehiclesTextColor = baseContent.vehicles?.textColor || '#e2e8f0';
        const vehiclesCardBackgroundColor = baseContent.vehicles?.cardBackgroundColor || '#0f172a';
        const testimonialsBackgroundMode = baseContent.testimonials?.backgroundMode || 'gradient';
        const testimonialsBackgroundColor = baseContent.testimonials?.backgroundColor || surfaceColor;
        const testimonialsGradientColor = baseContent.testimonials?.gradientColor || surfaceColor;
        const testimonialsTextColor = baseContent.testimonials?.textColor || '#e2e8f0';
        const whyBackgroundMode = baseContent.why?.backgroundMode || 'gradient';
        const whyBackgroundColor = baseContent.why?.backgroundColor || surfaceColor;
        const whyGradientColor = baseContent.why?.gradientColor || surfaceColor;
        const whyTextColor = baseContent.why?.textColor || '#e2e8f0';
        const photosBackgroundMode = baseContent.photos?.backgroundMode || 'gradient';
        const photosBackgroundColor = baseContent.photos?.backgroundColor || surfaceColor;
        const photosGradientColor = baseContent.photos?.gradientColor || surfaceColor;
        const emailBackgroundMode = baseContent.email?.backgroundMode || 'gradient';
        const emailBackgroundColor = baseContent.email?.backgroundColor || surfaceColor;
        const emailGradientColor = baseContent.email?.gradientColor || surfaceColor;
        const emailTextColor = baseContent.email?.textColor || '#e2e8f0';
        const footerBackgroundMode = baseContent.footer?.backgroundMode || 'gradient';
        const footerBackgroundColor = baseContent.footer?.backgroundColor || surfaceColor;
        const footerGradientColor = baseContent.footer?.gradientColor || surfaceColor;
        const footerTextColor = baseContent.footer?.textColor || '#e2e8f0';
        const vehicleItems = (baseContent.vehicles?.items || []).map((item) => {
            const rawPrice = item.price;
            const numericPrice = typeof rawPrice === 'number'
                ? rawPrice
                : Number(String(rawPrice || '').replace(/[^0-9.]/g, ''));
            const cleanedRate = String(item.rate || 'Day').replace(/^\//, '');
            return {
                ...item,
                price: Number.isFinite(numericPrice) ? numericPrice : '',
                rate: cleanedRate || 'Day',
                showOnHomepage: item.showOnHomepage !== false,
            };
        });
        return {
            ...baseContent,
            nav: {
                ...baseContent.nav,
                linkColors,
                ctaColor: baseContent.nav?.ctaColor || navAccent,
                backgroundMode: navBackgroundMode,
                backgroundColor: navBackgroundColor,
                gradientColor: navGradientColor,
            },
            hero: {
                ...baseContent.hero,
                ctaPrimaryColor: baseContent.hero?.ctaPrimaryColor || heroAccent,
                ctaSecondaryColor: baseContent.hero?.ctaSecondaryColor || heroAccent,
                backgroundMediaType: heroBackgroundMediaType,
                backgroundMediaUrl: heroBackgroundMediaUrl,
            },
            icons: {
                ...baseContent.icons,
                iconSize: baseContent.icons?.iconSize || 52,
                ctaColor: baseContent.icons?.ctaColor || iconsAccent,
                backgroundMode: iconsBackgroundMode,
                backgroundColor: iconsBackgroundColor,
                gradientColor: iconsGradientColor,
                textColor: iconsTextColor,
                iconColor: iconsIconColor,
            },
            adBanner: {
                ...baseContent.adBanner,
                accent: adBannerAccent,
                backgroundMode: adBannerBackgroundMode,
                backgroundColor: adBannerBackgroundColor,
                gradientColor: adBannerGradientColor,
                textColor: adBannerTextColor,
            },
            vehicles: {
                ...baseContent.vehicles,
                items: vehicleItems,
                backgroundMode: vehiclesBackgroundMode,
                backgroundColor: vehiclesBackgroundColor,
                gradientColor: vehiclesGradientColor,
                textColor: vehiclesTextColor,
                cardBackgroundColor: vehiclesCardBackgroundColor,
            },
            testimonials: {
                ...baseContent.testimonials,
                backgroundMode: testimonialsBackgroundMode,
                backgroundColor: testimonialsBackgroundColor,
                gradientColor: testimonialsGradientColor,
                textColor: testimonialsTextColor,
            },
            why: {
                ...baseContent.why,
                backgroundMode: whyBackgroundMode,
                backgroundColor: whyBackgroundColor,
                gradientColor: whyGradientColor,
                textColor: whyTextColor,
            },
            photos: {
                ...baseContent.photos,
                backgroundMode: photosBackgroundMode,
                backgroundColor: photosBackgroundColor,
                gradientColor: photosGradientColor,
            },
            email: {
                ...baseContent.email,
                backgroundMode: emailBackgroundMode,
                backgroundColor: emailBackgroundColor,
                gradientColor: emailGradientColor,
                textColor: emailTextColor,
            },
            footer: {
                ...baseContent.footer,
                backgroundMode: footerBackgroundMode,
                backgroundColor: footerBackgroundColor,
                gradientColor: footerGradientColor,
                textColor: footerTextColor,
            },
        };
    });
    const [openControl, setOpenControl] = useState('hero');
    const [generalOpen, setGeneralOpen] = useState(true);
    const [isSavingPreview, setIsSavingPreview] = useState(false);
    const [previewError, setPreviewError] = useState('');
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [logoUploadError, setLogoUploadError] = useState('');
    const [previewMode, setPreviewMode] = useState('desktop');
    const [vehicleImageErrors, setVehicleImageErrors] = useState([]);
    const testimonials = content?.testimonials?.items?.length
        ? content.testimonials.items
        : fallbackTestimonials;
    const whyItems = (content?.why?.items || []).map((item, index) => ({
        ...item,
        icon: item.icon || fallbackWhyIcons[index % fallbackWhyIcons.length],
    }));
    const photoItems = content?.photos?.items?.length ? content.photos.items : [];
    const theme = content?.theme || initialContent.theme;
    const logoStyles = {
        width: content?.nav?.logoWidth ? `${content.nav.logoWidth}px` : undefined,
        height: content?.nav?.logoHeight ? `${content.nav.logoHeight}px` : 'auto',
    };
    const navAccent = content?.nav?.accent || '#6366f1';
    const navBackgroundMode = content?.nav?.backgroundMode || 'gradient';
    const navBackgroundColor = content?.nav?.backgroundColor || surfaceColor;
    const navGradientColor = content?.nav?.gradientColor || surfaceColor;
    const navLinkColors = Array.isArray(content?.nav?.linkColors) && content.nav.linkColors.length
        ? content.nav.linkColors
        : (content?.nav?.links || []).map(() => navAccent);
    const navCtaColor = content?.nav?.ctaColor || navAccent;
    const getNavLinkColor = (index) => navLinkColors[index] || navAccent;
    const navBackground = navBackgroundMode === 'solid'
        ? navBackgroundColor
        : `linear-gradient(90deg, ${navAccent}18, ${navGradientColor})`;
    const heroAccent = content?.hero?.accent || '#7c3aed';
    const heroPrimaryCtaColor = content?.hero?.ctaPrimaryColor || heroAccent;
    const heroSecondaryCtaColor = content?.hero?.ctaSecondaryColor || heroAccent;
    const heroBackgroundMediaType = content?.hero?.backgroundMediaType || 'video';
    const heroBackgroundMediaUrl = content?.hero?.backgroundMediaUrl
        || 'https://www.fiveguycarrentals.com/theme/img/videoplayback.mp4';
    const iconsAccent = content?.icons?.accent || '#7c3aed';
    const iconsBackgroundMode = content?.icons?.backgroundMode || 'gradient';
    const iconsBackgroundColor = content?.icons?.backgroundColor || surfaceColor;
    const iconsGradientColor = content?.icons?.gradientColor || surfaceColor;
    const iconsTextColor = content?.icons?.textColor || '#e2e8f0';
    const iconsIconColor = content?.icons?.iconColor || iconsAccent;
    const iconCtaColor = content?.icons?.ctaColor || iconsAccent;
    const iconSizeValue = Number(content?.icons?.iconSize);
    const iconSize = Number.isFinite(iconSizeValue) && iconSizeValue > 0 ? iconSizeValue : 52;
    const iconFontSize = Math.max(12, Math.round(iconSize * 0.45));
    const getIconColor = () => iconsIconColor;
    const iconsBackground = iconsBackgroundMode === 'solid'
        ? iconsBackgroundColor
        : `linear-gradient(135deg, ${iconsAccent}1f, ${iconsGradientColor})`;
    const adBannerAccent = content?.adBanner?.accent || theme?.accent || '#7c3aed';
    const adBannerBackgroundMode = content?.adBanner?.backgroundMode || 'gradient';
    const adBannerBackgroundColor = content?.adBanner?.backgroundColor || surfaceColor;
    const adBannerGradientColor = content?.adBanner?.gradientColor || surfaceColor;
    const adBannerTextColor = content?.adBanner?.textColor || '#e2e8f0';
    const adBannerBackground = adBannerBackgroundMode === 'solid'
        ? adBannerBackgroundColor
        : `linear-gradient(90deg, ${adBannerAccent}18, ${adBannerGradientColor})`;
    const vehiclesBackgroundMode = content?.vehicles?.backgroundMode || 'gradient';
    const vehiclesBackgroundColor = content?.vehicles?.backgroundColor || surfaceColor;
    const vehiclesGradientColor = content?.vehicles?.gradientColor || surfaceColor;
    const vehiclesTextColor = content?.vehicles?.textColor || '#e2e8f0';
    const vehiclesCardBackgroundColor = content?.vehicles?.cardBackgroundColor || '#0f172a';
    const vehiclesBackground = vehiclesBackgroundMode === 'solid'
        ? vehiclesBackgroundColor
        : `linear-gradient(135deg, ${content.vehicles.accent}16, ${vehiclesGradientColor})`;
    const testimonialsBackgroundMode = content?.testimonials?.backgroundMode || 'gradient';
    const testimonialsBackgroundColor = content?.testimonials?.backgroundColor || surfaceColor;
    const testimonialsGradientColor = content?.testimonials?.gradientColor || surfaceColor;
    const testimonialsTextColor = content?.testimonials?.textColor || '#e2e8f0';
    const testimonialsBackground = testimonialsBackgroundMode === 'solid'
        ? testimonialsBackgroundColor
        : `linear-gradient(135deg, ${content.testimonials.accent}16, ${testimonialsGradientColor})`;
    const whyBackgroundMode = content?.why?.backgroundMode || 'gradient';
    const whyBackgroundColor = content?.why?.backgroundColor || surfaceColor;
    const whyGradientColor = content?.why?.gradientColor || surfaceColor;
    const whyTextColor = content?.why?.textColor || '#e2e8f0';
    const whyBackground = whyBackgroundMode === 'solid'
        ? whyBackgroundColor
        : `linear-gradient(135deg, ${content.why.accent}16, ${whyGradientColor})`;
    const photosBackgroundMode = content?.photos?.backgroundMode || 'gradient';
    const photosBackgroundColor = content?.photos?.backgroundColor || surfaceColor;
    const photosGradientColor = content?.photos?.gradientColor || surfaceColor;
    const photosBackground = photosBackgroundMode === 'solid'
        ? photosBackgroundColor
        : `linear-gradient(135deg, ${content.photos.accent}16, ${photosGradientColor})`;
    const emailBackgroundMode = content?.email?.backgroundMode || 'gradient';
    const emailBackgroundColor = content?.email?.backgroundColor || surfaceColor;
    const emailGradientColor = content?.email?.gradientColor || surfaceColor;
    const emailTextColor = content?.email?.textColor || '#e2e8f0';
    const emailBackground = emailBackgroundMode === 'solid'
        ? emailBackgroundColor
        : `linear-gradient(135deg, ${content.email.accent}16, ${emailGradientColor})`;
    const footerBackgroundMode = content?.footer?.backgroundMode || 'gradient';
    const footerBackgroundColor = content?.footer?.backgroundColor || surfaceColor;
    const footerGradientColor = content?.footer?.gradientColor || surfaceColor;
    const footerTextColor = content?.footer?.textColor || '#e2e8f0';
    const footerBackground = footerBackgroundMode === 'solid'
        ? footerBackgroundColor
        : `linear-gradient(135deg, ${content.footer.accent}16, ${footerGradientColor})`;
    const formatVehiclePrice = (price, rate) => {
        if (price === '' || price === null || typeof price === 'undefined') {
            return '';
        }
        const numericPrice = typeof price === 'number'
            ? price
            : Number(String(price).replace(/[^0-9.]/g, ''));
        if (!Number.isFinite(numericPrice)) {
            return '';
        }
        const cleanedRate = String(rate || 'Day').replace(/^\//, '') || 'Day';
        return `$${numericPrice}/${cleanedRate}`;
    };
    const toggleSection = (key) => {
        setSections((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const toggleControlVisibility = (key) => {
        setOpenControl((prev) => (prev === key ? '' : key));
    };

    const updateNavLink = (index, value) => {
        setContent((prev) => {
            const links = [...prev.nav.links];
            links[index] = value;
            return {
                ...prev,
                nav: { ...prev.nav, links },
            };
        });
    };

    const updateNavLinkColor = (index, value) => {
        setContent((prev) => {
            const linkColors = [...(prev.nav.linkColors || [])];
            linkColors[index] = value;
            return {
                ...prev,
                nav: { ...prev.nav, linkColors },
            };
        });
    };

    const updateNavCtaColor = (value) => {
        setContent((prev) => ({
            ...prev,
            nav: { ...prev.nav, ctaColor: value },
        }));
    };

    const updateHeroCtaColor = (field, value) => {
        setContent((prev) => ({
            ...prev,
            hero: { ...prev.hero, [field]: value },
        }));
    };

    const updateIconsCtaColor = (value) => {
        setContent((prev) => ({
            ...prev,
            icons: { ...prev.icons, ctaColor: value },
        }));
    };

    const updateIconSize = (value) => {
        setContent((prev) => ({
            ...prev,
            icons: { ...prev.icons, iconSize: value },
        }));
    };

    const updateVehicleVisibility = (index, value) => {
        setContent((prev) => {
            const updated = [...prev.vehicles.items];
            updated[index] = { ...updated[index], showOnHomepage: value };
            return {
                ...prev,
                vehicles: {
                    ...prev.vehicles,
                    items: updated,
                },
            };
        });
    };

    const addVehicle = () => {
        setContent((prev) => ({
            ...prev,
            vehicles: {
                ...prev.vehicles,
                items: [
                    ...prev.vehicles.items,
                    {
                        title: 'New vehicle',
                        price: '',
                        rate: '',
                        note: '',
                        description: '',
                        image: '',
                        backgroundColor: '',
                        showOnHomepage: true,
                    },
                ],
            },
        }));
        setVehicleImageErrors((prev) => [...prev, '']);
    };

    const removeVehicle = (index) => {
        setContent((prev) => {
            const updated = [...prev.vehicles.items];
            updated.splice(index, 1);
            return {
                ...prev,
                vehicles: {
                    ...prev.vehicles,
                    items: updated,
                },
            };
        });
        setVehicleImageErrors((prev) => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    };

    const handleVehicleImageUpload = (index, event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            return;
        }
        if (!file.type.startsWith('image/')) {
            event.target.value = '';
            setVehicleImageErrors((prev) => {
                const updated = [...prev];
                updated[index] = 'Please upload a valid image file.';
                return updated;
            });
            return;
        }
        if (file.size > maxVehicleImageBytes) {
            event.target.value = '';
            setVehicleImageErrors((prev) => {
                const updated = [...prev];
                updated[index] = 'Image is too large. Max size is 3MB.';
                return updated;
            });
            return;
        }
        setVehicleImageErrors((prev) => {
            const updated = [...prev];
            updated[index] = '';
            return updated;
        });
        const reader = new FileReader();
        reader.onload = () => {
            updateArrayItem('vehicles', index, 'image', reader.result);
            event.target.value = '';
        };
        reader.onerror = () => {
            setVehicleImageErrors((prev) => {
                const updated = [...prev];
                updated[index] = 'Unable to upload image. Please try again.';
                return updated;
            });
            event.target.value = '';
        };
        reader.readAsDataURL(file);
    };

    const applyThemeAccent = (prev, accent) => ({
        ...prev,
        nav: {
            ...prev.nav,
            accent,
            linkColors: (prev.nav?.links || []).map(() => accent),
            ctaColor: accent,
        },
        hero: {
            ...prev.hero,
            accent,
            ctaPrimaryColor: accent,
            ctaSecondaryColor: accent,
        },
        icons: {
            ...prev.icons,
            accent,
            iconColors: (prev.icons?.items || []).map(() => accent),
            ctaColor: accent,
        },
        vehicles: { ...prev.vehicles, accent },
        testimonials: { ...prev.testimonials, accent },
        why: { ...prev.why, accent },
        photos: { ...prev.photos, accent },
        email: { ...prev.email, accent },
        adBanner: { ...prev.adBanner, accent },
        footer: { ...prev.footer, accent },
    });

    const applyThemePreset = (preset) => {
        setContent((prev) => {
            const palette = preset.palette || {};
            const surfaces = preset.surfaces || {};
            const navAccent = palette.nav || preset.accent;
            const heroAccent = palette.hero || preset.accent;
            const heroSecondary = palette.heroSecondary || heroAccent;
            const iconsAccent = palette.icons || preset.accent;
            const vehiclesAccent = palette.vehicles || preset.accent;
            const testimonialsAccent = palette.testimonials || preset.accent;
            const whyAccent = palette.why || preset.accent;
            const photosAccent = palette.photos || preset.accent;
            const emailAccent = palette.email || preset.accent;
            const adBannerAccent = palette.adBanner || preset.accent;
            const footerAccent = palette.footer || preset.accent;
            const navSurface = surfaces.nav || surfaceColor;
            const iconsSurface = surfaces.icons || surfaceColor;
            const whySurface = surfaces.why || surfaceColor;
            const emailSurface = surfaces.email || surfaceColor;
            const adBannerSurface = surfaces.adBanner || surfaceColor;
            const nextTheme = {
                ...(prev.theme || initialContent.theme),
                name: preset.name,
                accent: preset.accent,
                background: preset.background,
            };
            return {
                ...prev,
                theme: nextTheme,
                nav: {
                    ...prev.nav,
                    accent: navAccent,
                    linkColors: (prev.nav?.links || []).map(() => navAccent),
                    ctaColor: navAccent,
                    backgroundMode: 'solid',
                    backgroundColor: navSurface,
                },
                hero: {
                    ...prev.hero,
                    accent: heroAccent,
                    ctaPrimaryColor: heroAccent,
                    ctaSecondaryColor: heroSecondary,
                },
                icons: {
                    ...prev.icons,
                    accent: iconsAccent,
                    iconColors: (prev.icons?.items || []).map(() => iconsAccent),
                    ctaColor: iconsAccent,
                    backgroundMode: 'solid',
                    backgroundColor: iconsSurface,
                },
                vehicles: { ...prev.vehicles, accent: vehiclesAccent },
                testimonials: { ...prev.testimonials, accent: testimonialsAccent },
                why: {
                    ...prev.why,
                    accent: whyAccent,
                    backgroundMode: 'solid',
                    backgroundColor: whySurface,
                },
                photos: { ...prev.photos, accent: photosAccent },
                email: {
                    ...prev.email,
                    accent: emailAccent,
                    backgroundMode: 'solid',
                    backgroundColor: emailSurface,
                },
                adBanner: {
                    ...prev.adBanner,
                    accent: adBannerAccent,
                    backgroundMode: 'solid',
                    backgroundColor: adBannerSurface,
                },
                footer: { ...prev.footer, accent: footerAccent },
            };
        });
    };

    const updateThemeAccent = (accent) => {
        setContent((prev) => ({
            ...applyThemeAccent(prev, accent),
            theme: {
                ...(prev.theme || initialContent.theme),
                name: 'Custom',
                accent,
            },
        }));
    };

    const updateThemeBackground = (background) => {
        setContent((prev) => ({
            ...prev,
            theme: {
                ...(prev.theme || initialContent.theme),
                name: 'Custom',
                background,
            },
        }));
    };

    const handleLogoUploadToFirestore = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            return;
        }
        if (!file.type.startsWith('image/')) {
            event.target.value = '';
            setLogoUploadError('Please upload a valid image file.');
            return;
        }
        if (file.size > maxLogoSizeBytes) {
            event.target.value = '';
            setLogoUploadError('Logo is too large. Max size is 2MB.');
            return;
        }
        setLogoUploadError('');
        setIsUploadingLogo(true);
        const reader = new FileReader();
        reader.onload = () => {
            updateContentField('nav', 'logo', reader.result);
            setIsUploadingLogo(false);
            event.target.value = '';
        };
        reader.onerror = () => {
            setLogoUploadError('Unable to upload logo. Please try again.');
            setIsUploadingLogo(false);
            event.target.value = '';
        };
        reader.readAsDataURL(file);
    };

    const updateContentField = (sectionKey, field, value) => {
        setContent((prev) => ({
            ...prev,
            [sectionKey]: {
                ...prev[sectionKey],
                [field]: value,
            },
        }));
    };

    const updateArrayItem = (sectionKey, index, field, value) => {
        setContent((prev) => {
            const updated = [...prev[sectionKey].items];
            updated[index] = { ...updated[index], [field]: value };
            return {
                ...prev,
                [sectionKey]: {
                    ...prev[sectionKey],
                    items: updated,
                },
            };
        });
    };

    const updatePhotoItem = (index, field, value) => {
        setContent((prev) => {
            const updatedItems = [...(prev.photos.items || [])];
            updatedItems[index] = { ...(updatedItems[index] || {}), [field]: value };
            return {
                ...prev,
                photos: {
                    ...prev.photos,
                    items: updatedItems,
                },
            };
        });
    };

    const goBack = () => {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            window.location.href = '/website-builder';
        }
    };

    const openPreviewPage = async () => {
        if (isSavingPreview) {
            return;
        }
        setPreviewError('');
        setIsSavingPreview(true);
        try {
            const previewRef = doc(collection(db, 'previews'));
            const previewId = previewRef.id;
            await setDoc(previewRef, {
                content: {
                    ...content,
                    nav: {
                        ...content.nav,
                        logo: content?.nav?.logo || '',
                    },
                },
                sections,
                updatedAt: serverTimestamp(),
            });

            navigate(`/website-builder/live-preview?previewId=${previewId}`);
        } catch (error) {
            console.error('Failed to save preview', error);
            setPreviewError('Unable to save preview. Please try again.');
        } finally {
            setIsSavingPreview(false);
        }
    };
    return (
        <div
            className="live-demo-page controls-modern"
            style={{
                background: theme.background,
                color: theme.text || '#e2e8f0',
            }}
        >
            <div className="live-demo-topbar">
                <div className="topbar-left">
                    <button className="back-button" onClick={goBack} aria-label="Go back">
                        &lt; Back
                    </button>
                </div>
                <div className="topbar-center">
                    <span className="title-text">Website Builder</span>
                    <span className="status-dot" aria-label="Live status"></span>
                </div>
                <div className="topbar-right">
                    <button
                        type="button"
                        className="preview-button"
                        onClick={openPreviewPage}
                        aria-label="Open preview page"
                        disabled={isSavingPreview}
                    >
                        {isSavingPreview ? 'Saving...' : 'Preview'}
                    </button>
                </div>
            </div>

            <section className="live-demo-workbench">
                <div className={`container live-demo-grid ${openControl === 'vehicles' ? 'fleet-open' : ''}`}>
                    <div className="controls-panel">
                        {previewError && (
                            <div className="preview-banner" role="alert">
                                {previewError}
                            </div>
                        )}
                        <div className="controls-group">
                            <div className="controls-header">
                                <p className="controls-eyebrow">Logo</p>
                                <span>Upload your brand logo to use in the header</span>
                            </div>
                            <div className="logo-upload">
                                <div className="accordion-field">
                                    <label>Logo image</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoUploadToFirestore}
                                        disabled={isUploadingLogo}
                                    />
                                    {logoUploadError && (
                                        <span className="helper-text" style={{ color: '#fca5a5' }}>
                                            {logoUploadError}
                                        </span>
                                    )}
                                </div>
                                {content.nav.logo && (
                                    <div className="logo-preview">
                                        <img src={content.nav.logo} alt="Uploaded logo preview" style={logoStyles} />
                                        <button
                                            type="button"
                                            className="btn btn-outline btn-small"
                                            onClick={() => updateContentField('nav', 'logo', '')}
                                        >
                                            Remove logo
                                        </button>
                                    </div>
                                )}
                                {content.nav.logo && (
                                    <div className="logo-size-grid">
                                        <div className="accordion-field">
                                            <label>Logo width (px)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="Auto"
                                                value={content.nav.logoWidth ?? ''}
                                                onChange={(e) =>
                                                    updateContentField(
                                                        'nav',
                                                        'logoWidth',
                                                        e.target.value === '' ? '' : Number(e.target.value)
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="accordion-field">
                                            <label>Logo height (px)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="Auto"
                                                value={content.nav.logoHeight ?? ''}
                                                onChange={(e) =>
                                                    updateContentField(
                                                        'nav',
                                                        'logoHeight',
                                                        e.target.value === '' ? '' : Number(e.target.value)
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="controls-group">
                            <div className={`accordion general-accordion ${generalOpen ? 'open' : ''}`}>
                                <button
                                    type="button"
                                    className="accordion-header general-accordion-header"
                                    onClick={() => setGeneralOpen((prev) => !prev)}
                                    aria-expanded={generalOpen}
                                >
                                    <div className="general-accordion-title">
                                        <p className="controls-eyebrow">General</p>
                                        <span>Choose a theme or customize your colors</span>
                                    </div>
                                    <span className="accordion-caret">{generalOpen ? '-' : '+'}</span>
                                </button>
                                {generalOpen && (
                                    <div className="accordion-body general-accordion-body">
                                        <p className="helper-text" style={{ margin: 0 }}>
                                            All colors can be customized in each page section.
                                        </p>
                                        <div className="pill-group">
                                            {themePresets.map((preset) => (
                                                <button
                                                    key={preset.name}
                                                    type="button"
                                                    className={`pill ${theme?.name === preset.name ? 'active' : ''}`}
                                                    onClick={() => applyThemePreset(preset)}
                                                >
                                                    {preset.name}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className={`pill ${theme?.name === 'Custom' ? 'active' : ''}`}
                                                onClick={() =>
                                                    setContent((prev) => ({
                                                        ...prev,
                                                        theme: {
                                                            ...(prev.theme || initialContent.theme),
                                                            name: 'Custom',
                                                        },
                                                    }))
                                                }
                                            >
                                                Custom
                                            </button>
                                        </div>
                                        <div className="input-inline">
                                            <div className="accordion-field">
                                                <label>Accent</label>
                                                <input
                                                    type="color"
                                                    value={theme?.accent || '#7c3aed'}
                                                    onChange={(e) => updateThemeAccent(e.target.value)}
                                                />
                                            </div>
                                            <div className="accordion-field">
                                                <label>Background</label>
                                                <input
                                                    type="color"
                                                    value={theme?.background || '#0b1020'}
                                                    onChange={(e) => updateThemeBackground(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="controls-group">
                            <div className="controls-header">
                                <p className="controls-eyebrow">Page sections</p>
                                <span>Toggle visibility and customize each block</span>
                            </div>
                            <div className="accordion-list">
                                {Object.keys(sectionLabels).map((key) => (
                                    <div
                                        key={key}
                                        className={`accordion ${openControl === key ? 'open' : ''} ${
                                            sections[key] ? '' : 'muted'
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className="accordion-header"
                                            onClick={() => toggleControlVisibility(key)}
                                            aria-expanded={openControl === key}
                                        >
                                            <span className="accordion-label">{sectionLabels[key]}</span>
                                            <div className="accordion-actions">
                                                <label className="toggle-row">
                                                    <input
                                                        type="checkbox"
                                                        checked={sections[key]}
                                                        onChange={() => toggleSection(key)}
                                                        aria-label={`Toggle ${sectionLabels[key]} visibility`}
                                                    />
                                                </label>
                                                <span className="accordion-caret">{openControl === key ? '-' : '+'}</span>
                                            </div>
                                        </button>
                                        {openControl === key && (
                                            <div className="accordion-body">
                                                <div className="accordion-field">
                                                    <label>Accent color</label>
                                                    <input
                                                        type="color"
                                                        value={content[key].accent}
                                                        onChange={(e) => updateContentField(key, 'accent', e.target.value)}
                                                    />
                                                </div>

                                                {key === 'nav' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={navBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('nav', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {navBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={navBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('nav', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {navBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={navGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('nav', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="accordion-field">
                                                            <label>Brand</label>
                                                            <input
                                                                type="text"
                                                                value={content.nav.brand}
                                                                onChange={(e) =>
                                                                    updateContentField('nav', 'brand', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Badge label</label>
                                                            <input
                                                                type="text"
                                                                value={content.nav.badge}
                                                                onChange={(e) =>
                                                                    updateContentField('nav', 'badge', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="input-inline">
                                                            <div className="accordion-field">
                                                                <label>CTA label</label>
                                                                <input
                                                                    type="text"
                                                                    value={content.nav.cta}
                                                                    onChange={(e) =>
                                                                        updateContentField('nav', 'cta', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="accordion-field">
                                                                <label>CTA color</label>
                                                                <input
                                                                    type="color"
                                                                    value={navCtaColor}
                                                                    onChange={(e) => updateNavCtaColor(e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="accordion-field stacked">
                                                            <label>Menu links</label>
                                                            <div style={{ display: 'grid', gap: '10px' }}>
                                                                {content.nav.links.map((link, index) => (
                                                                    <div
                                                                        key={index}
                                                                        style={{
                                                                            display: 'grid',
                                                                            gridTemplateColumns: '1fr auto',
                                                                            gap: '10px',
                                                                            alignItems: 'center',
                                                                        }}
                                                                    >
                                                                        <input
                                                                            type="text"
                                                                            value={link}
                                                                            onChange={(e) => updateNavLink(index, e.target.value)}
                                                                            aria-label={`Navigation link ${index + 1}`}
                                                                        />
                                                                        <input
                                                                            type="color"
                                                                            value={getNavLinkColor(index)}
                                                                            onChange={(e) => updateNavLinkColor(index, e.target.value)}
                                                                            aria-label={`Navigation link ${index + 1} color`}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                {key === 'hero' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Headline</label>
                                                            <input
                                                                type="text"
                                                                value={content.hero.headline}
                                                                onChange={(e) =>
                                                                    updateContentField('hero', 'headline', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Subhead</label>
                                                            <textarea
                                                                value={content.hero.subhead}
                                                                onChange={(e) =>
                                                                    updateContentField('hero', 'subhead', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Background media</label>
                                                            <select
                                                                value={heroBackgroundMediaType}
                                                                onChange={(e) =>
                                                                    updateContentField('hero', 'backgroundMediaType', e.target.value)
                                                                }
                                                            >
                                                                <option value="video">Video</option>
                                                                <option value="image">Image</option>
                                                                <option value="none">None</option>
                                                            </select>
                                                        </div>
                                                        {heroBackgroundMediaType !== 'none' && (
                                                            <div className="accordion-field">
                                                                <label>{heroBackgroundMediaType === 'video' ? 'Video URL' : 'Image URL'}</label>
                                                                <input
                                                                    type="text"
                                                                    value={heroBackgroundMediaUrl}
                                                                    onChange={(e) =>
                                                                        updateContentField('hero', 'backgroundMediaUrl', e.target.value)
                                                                    }
                                                                    placeholder="https://"
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="input-inline">
                                                        <div className="accordion-field">
                                                            <label>Primary CTA</label>
                                                            <input
                                                                type="text"
                                                                value={content.hero.ctaPrimary}
                                                                onChange={(e) =>
                                                                    updateContentField('hero', 'ctaPrimary', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Primary CTA color</label>
                                                            <input
                                                                type="color"
                                                                value={heroPrimaryCtaColor}
                                                                onChange={(e) => updateHeroCtaColor('ctaPrimaryColor', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                        <div className="input-inline">
                                                        <div className="accordion-field">
                                                            <label>Secondary CTA</label>
                                                            <input
                                                                type="text"
                                                                value={content.hero.ctaSecondary}
                                                                onChange={(e) =>
                                                                    updateContentField('hero', 'ctaSecondary', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Secondary CTA color</label>
                                                            <input
                                                                type="color"
                                                                value={heroSecondaryCtaColor}
                                                                onChange={(e) => updateHeroCtaColor('ctaSecondaryColor', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    </>
                                                )}

                                                {key === 'icons' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={iconsBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('icons', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {iconsBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={iconsBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('icons', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {iconsBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={iconsGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('icons', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="accordion-field">
                                                            <label>Icon color</label>
                                                            <input
                                                                type="color"
                                                                value={iconsIconColor}
                                                                onChange={(e) =>
                                                                    updateContentField('icons', 'iconColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Text color</label>
                                                            <input
                                                                type="color"
                                                                value={iconsTextColor}
                                                                onChange={(e) =>
                                                                    updateContentField('icons', 'textColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Section title</label>
                                                            <input
                                                                type="text"
                                                                value={content.icons.title}
                                                                onChange={(e) => updateContentField('icons', 'title', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="input-inline">
                                                            <div className="accordion-field">
                                                                <label>CTA label</label>
                                                                <input
                                                                    type="text"
                                                                    value={content.icons.ctaLabel}
                                                                    onChange={(e) => updateContentField('icons', 'ctaLabel', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="accordion-field">
                                                                <label>CTA color</label>
                                                                <input
                                                                    type="color"
                                                                    value={iconCtaColor}
                                                                    onChange={(e) => updateIconsCtaColor(e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Icon size (px)</label>
                                                            <input
                                                                type="number"
                                                                min="20"
                                                                value={content.icons.iconSize ?? ''}
                                                                onChange={(e) =>
                                                                    updateIconSize(e.target.value === '' ? '' : Number(e.target.value))
                                                                }
                                                            />
                                                        </div>
                                                        {content.icons.items.map((item, index) => (
                                                            <div key={index} className="accordion-field stacked">
                                                                <label>Icon {index + 1}</label>
                                                                <div className="input-inline">
                                                                    <select
                                                                        value={item.icon}
                                                                        onChange={(e) =>
                                                                            updateArrayItem('icons', index, 'icon', e.target.value)
                                                                        }
                                                                    >
                                                                        {iconChoices.map((choice) => (
                                                                            <option key={choice} value={choice}>
                                                                                {choice}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <input
                                                                        type="text"
                                                                        value={item.title}
                                                                        onChange={(e) =>
                                                                            updateArrayItem('icons', index, 'title', e.target.value)
                                                                        }
                                                                    />
                                                                </div>
                                                                <div className="input-inline">
                                                                    <input
                                                                        type="text"
                                                                        value={item.description}
                                                                        onChange={(e) =>
                                                                            updateArrayItem('icons', index, 'description', e.target.value)
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </>
                                                )}

                                                {key === 'vehicles' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={vehiclesBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('vehicles', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {vehiclesBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={vehiclesBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('vehicles', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {vehiclesBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={vehiclesGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('vehicles', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="accordion-field">
                                                            <label>Text color</label>
                                                            <input
                                                                type="color"
                                                                value={vehiclesTextColor}
                                                                onChange={(e) =>
                                                                    updateContentField('vehicles', 'textColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Default card color</label>
                                                            <input
                                                                type="color"
                                                                value={vehiclesCardBackgroundColor}
                                                                onChange={(e) =>
                                                                    updateContentField('vehicles', 'cardBackgroundColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Badge label</label>
                                                            <input
                                                                type="text"
                                                                value={content.vehicles.badge}
                                                                onChange={(e) =>
                                                                    updateContentField('vehicles', 'badge', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Section heading</label>
                                                            <input
                                                                type="text"
                                                                value={content.vehicles.heading}
                                                                onChange={(e) =>
                                                                    updateContentField('vehicles', 'heading', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Section copy</label>
                                                            <textarea
                                                                value={content.vehicles.subhead}
                                                                onChange={(e) =>
                                                                    updateContentField('vehicles', 'subhead', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        {content.vehicles.items.map((item, index) => (
                                                            <div key={index} className="vehicle-config">
                                                                <div className="vehicle-config-header">
                                                                    <label>Vehicle {index + 1}</label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline btn-small"
                                                                        onClick={() => removeVehicle(index)}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                                <div className="accordion-field">
                                                                    <label>Name</label>
                                                                    <input
                                                                        type="text"
                                                                        value={item.title}
                                                                        onChange={(e) =>
                                                                            updateArrayItem('vehicles', index, 'title', e.target.value)
                                                                        }
                                                                    />
                                                                </div>
                                                                <div className="accordion-field">
                                                                    <label>Card background</label>
                                                                    <input
                                                                        type="color"
                                                                        value={item.backgroundColor || vehiclesCardBackgroundColor}
                                                                        onChange={(e) =>
                                                                            updateArrayItem('vehicles', index, 'backgroundColor', e.target.value)
                                                                        }
                                                                    />
                                                                </div>
                                                                <div className="vehicle-pricing-row">
                                                                    <div className="accordion-field">
                                                                        <label>Price</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="1"
                                                                            value={item.price ?? ''}
                                                                            onChange={(e) =>
                                                                                updateArrayItem(
                                                                                    'vehicles',
                                                                                    index,
                                                                                    'price',
                                                                                    e.target.value === '' ? '' : Number(e.target.value)
                                                                                )
                                                                            }
                                                                            placeholder="65"
                                                                        />
                                                                    </div>
                                                                    <div className="accordion-field">
                                                                        <label>Rate</label>
                                                                        <select
                                                                            value={item.rate || 'Day'}
                                                                            onChange={(e) =>
                                                                                updateArrayItem('vehicles', index, 'rate', e.target.value)
                                                                            }
                                                                        >
                                                                            {['Day', 'Week', 'Month'].map((rate) => (
                                                                                <option key={rate} value={rate}>
                                                                                    {rate}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <div className="accordion-field">
                                                                        <label>Note</label>
                                                                        <input
                                                                            type="text"
                                                                            value={item.note || ''}
                                                                            onChange={(e) =>
                                                                                updateArrayItem('vehicles', index, 'note', e.target.value)
                                                                            }
                                                                            placeholder="Free Cancellation"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <label className="vehicle-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={item.showOnHomepage !== false}
                                                                        onChange={(e) => updateVehicleVisibility(index, e.target.checked)}
                                                                    />
                                                                    Show on homepage
                                                                </label>
                                                                <div className="vehicle-image-grid">
                                                                    <div className="accordion-field">
                                                                        <label>Image URL</label>
                                                                        <input
                                                                            type="text"
                                                                            value={item.image || ''}
                                                                            onChange={(e) =>
                                                                                updateArrayItem('vehicles', index, 'image', e.target.value)
                                                                            }
                                                                            placeholder="https://"
                                                                        />
                                                                    </div>
                                                                    <div className="accordion-field logo-upload">
                                                                        <label>Upload image</label>
                                                                        <input
                                                                            type="file"
                                                                            accept="image/*"
                                                                            onChange={(e) => handleVehicleImageUpload(index, e)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {vehicleImageErrors[index] && (
                                                                    <span className="helper-text" style={{ color: '#fca5a5' }}>
                                                                        {vehicleImageErrors[index]}
                                                                    </span>
                                                                )}
                                                                {item.image && (
                                                                    <div className="logo-preview vehicle-image-preview">
                                                                        <img src={item.image} alt={`${item.title || 'Vehicle'} preview`} />
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline btn-small"
                                                                            onClick={() => updateArrayItem('vehicles', index, 'image', '')}
                                                                        >
                                                                            Remove image
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                <div className="accordion-field">
                                                                    <label>Description</label>
                                                                    <input
                                                                        type="text"
                                                                        value={item.description || ''}
                                                                        onChange={(e) =>
                                                                            updateArrayItem('vehicles', index, 'description', e.target.value)
                                                                        }
                                                                        placeholder="Optional description"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline btn-small"
                                                            onClick={addVehicle}
                                                        >
                                                            Add vehicle
                                                        </button>
                                                    </>
                                                )}
                                                {key === 'testimonials' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={testimonialsBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('testimonials', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {testimonialsBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={testimonialsBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('testimonials', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {testimonialsBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={testimonialsGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('testimonials', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="accordion-field">
                                                            <label>Text color</label>
                                                            <input
                                                                type="color"
                                                                value={testimonialsTextColor}
                                                                onChange={(e) =>
                                                                    updateContentField('testimonials', 'textColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Quote</label>
                                                            <textarea
                                                                value={content.testimonials.quote}
                                                                onChange={(e) =>
                                                                    updateContentField('testimonials', 'quote', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Badge</label>
                                                            <input
                                                                type="text"
                                                                value={content.testimonials.badge}
                                                                onChange={(e) =>
                                                                    updateContentField('testimonials', 'badge', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        {testimonials.map((item, index) => (
                                                            <div key={item.name + index} className="accordion-field stacked">
                                                                <label>Testimonial {index + 1}</label>
                                                                <input
                                                                    type="text"
                                                                    value={item.name}
                                                                    onChange={(e) =>
                                                                        updateArrayItem('testimonials', index, 'name', e.target.value)
                                                                    }
                                                                    placeholder="Name"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={item.title}
                                                                    onChange={(e) =>
                                                                        updateArrayItem('testimonials', index, 'title', e.target.value)
                                                                    }
                                                                    placeholder="Role / Location"
                                                                />
                                                                <textarea
                                                                    value={item.quote}
                                                                    onChange={(e) =>
                                                                        updateArrayItem('testimonials', index, 'quote', e.target.value)
                                                                    }
                                                                    placeholder="Short quote"
                                                                    rows={3}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={item.image}
                                                                    onChange={(e) =>
                                                                        updateArrayItem('testimonials', index, 'image', e.target.value)
                                                                    }
                                                                    placeholder="Image URL"
                                                                />
                                                            </div>
                                                        ))}
                                                    </>
                                                )}

                                                {key === 'why' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={whyBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('why', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {whyBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={whyBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('why', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {whyBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={whyGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('why', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="accordion-field">
                                                            <label>Text color</label>
                                                            <input
                                                                type="color"
                                                                value={whyTextColor}
                                                                onChange={(e) =>
                                                                    updateContentField('why', 'textColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                {key === 'why' &&
                                                    content.why.items.map((item, index) => (
                                                        <div key={index} className="accordion-field stacked">
                                                            <label>Reason {index + 1}</label>
                                                            <input
                                                                type="text"
                                                                value={item.title}
                                                                onChange={(e) =>
                                                                    updateArrayItem('why', index, 'title', e.target.value)
                                                                }
                                                            />
                                                            <input
                                                                type="text"
                                                                value={item.description}
                                                                onChange={(e) =>
                                                                    updateArrayItem('why', index, 'description', e.target.value)
                                                                }
                                                            />
                                                            <input
                                                                type="text"
                                                                value={item.icon || ''}
                                                                onChange={(e) => updateArrayItem('why', index, 'icon', e.target.value)}
                                                                placeholder="Icon (emoji or short text)"
                                                            />
                                                        </div>
                                                    ))}
                                                {key === 'photos' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={photosBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('photos', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {photosBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={photosBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('photos', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {photosBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={photosGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('photos', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                                {key === 'photos' &&
                                                    photoItems.map((item, index) => (
                                                        <div key={index} className="accordion-field stacked">
                                                            <label>Photo {index + 1}</label>
                                                            <input
                                                                type="text"
                                                                value={item.image || ''}
                                                                onChange={(e) =>
                                                                    updatePhotoItem(index, 'image', e.target.value)
                                                                }
                                                                placeholder="Image URL"
                                                            />
                                                        </div>
                                                    ))}
                                                {key === 'email' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={emailBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('email', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {emailBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={emailBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('email', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {emailBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={emailGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('email', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="accordion-field">
                                                            <label>Text color</label>
                                                            <input
                                                                type="color"
                                                                value={emailTextColor}
                                                                onChange={(e) =>
                                                                    updateContentField('email', 'textColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                {key === 'email' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Placeholder</label>
                                                            <input
                                                                type="text"
                                                                value={content.email.placeholder}
                                                                onChange={(e) =>
                                                                    updateContentField('email', 'placeholder', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Button text</label>
                                                            <input
                                                                type="text"
                                                                value={content.email.buttonText}
                                                                onChange={(e) =>
                                                                    updateContentField('email', 'buttonText', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                {key === 'footer' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={footerBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {footerBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={footerBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('footer', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {footerBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={footerGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('footer', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="accordion-field">
                                                            <label>Text color</label>
                                                            <input
                                                                type="color"
                                                                value={footerTextColor}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'textColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}

                                                {key === 'footer' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Title</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.title}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'title', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Tagline</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.tagline}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'tagline', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Badge text</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.badgeText}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'badgeText', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Contact title</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.contactTitle}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'contactTitle', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Contact email</label>
                                                            <input
                                                                type="email"
                                                                className="accent-input"
                                                                value={content.footer.contactEmail}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'contactEmail', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Contact phone</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.contactPhone}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'contactPhone', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Hours title</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.hoursTitle}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'hoursTitle', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Hours days</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.hoursDays}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'hoursDays', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Hours time</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.hoursTime}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'hoursTime', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Follow title</label>
                                                            <input
                                                                type="text"
                                                                value={content.footer.followTitle}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'followTitle', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Instagram URL</label>
                                                            <input
                                                                type="url"
                                                                className="accent-input"
                                                                value={content.footer.instagramUrl}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'instagramUrl', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Facebook URL</label>
                                                            <input
                                                                type="url"
                                                                className="accent-input"
                                                                value={content.footer.facebookUrl}
                                                                onChange={(e) =>
                                                                    updateContentField('footer', 'facebookUrl', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                {key === 'adBanner' && (
                                                    <>
                                                        <div className="accordion-field">
                                                            <label>Background style</label>
                                                            <select
                                                                value={adBannerBackgroundMode}
                                                                onChange={(e) =>
                                                                    updateContentField('adBanner', 'backgroundMode', e.target.value)
                                                                }
                                                            >
                                                                <option value="gradient">Gradient</option>
                                                                <option value="solid">Solid</option>
                                                            </select>
                                                        </div>
                                                        {adBannerBackgroundMode === 'solid' && (
                                                            <div className="accordion-field">
                                                                <label>Background color</label>
                                                                <input
                                                                    type="color"
                                                                    value={adBannerBackgroundColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('adBanner', 'backgroundColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {adBannerBackgroundMode === 'gradient' && (
                                                            <div className="accordion-field">
                                                                <label>Gradient end color</label>
                                                                <input
                                                                    type="color"
                                                                    value={adBannerGradientColor}
                                                                    onChange={(e) =>
                                                                        updateContentField('adBanner', 'gradientColor', e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="accordion-field">
                                                            <label>Text color</label>
                                                            <input
                                                                type="color"
                                                                value={adBannerTextColor}
                                                                onChange={(e) =>
                                                                    updateContentField('adBanner', 'textColor', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Phone number</label>
                                                            <input
                                                                type="text"
                                                                value={content.adBanner.phone || ''}
                                                                onChange={(e) =>
                                                                    updateContentField('adBanner', 'phone', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Testimonial</label>
                                                            <textarea
                                                                value={content.adBanner.quote || ''}
                                                                onChange={(e) =>
                                                                    updateContentField('adBanner', 'quote', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        <div className="accordion-field">
                                                            <label>Author</label>
                                                            <input
                                                                type="text"
                                                                value={content.adBanner.author || ''}
                                                                onChange={(e) =>
                                                                    updateContentField('adBanner', 'author', e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div
                        className={`preview-panel preview-panel--demo ${previewMode === 'mobile' ? 'preview-mobile' : ''}`}
                        style={{
                            backgroundColor: surfaceColor,
                            borderColor: '#7c3aed',
                        }}
                    >
                        <div className="preview-toolbar">
                            <span className="dot"></span>
                            <span className="dot"></span>
                            <span className="dot"></span>
                            <div className="toolbar-chips">
                                <button
                                    type="button"
                                    className={`chip toolbar-button ${previewMode === 'desktop' ? 'active' : ''}`}
                                    aria-pressed={previewMode === 'desktop'}
                                    onClick={() => setPreviewMode('desktop')}
                                >
                                    Desktop
                                </button>
                                <button
                                    type="button"
                                    className={`chip toolbar-button ${previewMode === 'mobile' ? 'active' : ''}`}
                                    aria-pressed={previewMode === 'mobile'}
                                    onClick={() => setPreviewMode('mobile')}
                                >
                                    Mobile
                                </button>
                            </div>
                        </div>
                        {sections.adBanner && (
                            <div>
                                <div
                                    className={`ad-banner ${previewMode === 'mobile' ? 'ad-banner--stacked' : ''}`}
                                    style={{
                                        background: adBannerBackground,
                                        border: `1px solid ${adBannerAccent}30`,
                                        color: adBannerTextColor,
                                        fontSize: '0.95rem',
                                    }}
                                >
                                    <div className="ad-banner__phone" style={{ fontWeight: 700 }}>
                                        {content.adBanner.phone || '(833) 353-3847'}
                                    </div>
                                    <div className="ad-banner__quote" style={{ lineHeight: 1.4 }}>
                                        “{content.adBanner.quote}” — <span style={{ opacity: 0.85 }}>{content.adBanner.author}</span>
                                    </div>
                                    <div className="ad-banner__email" style={{ fontWeight: 600 }}>
                                        support@fleetifysoftware.com
                                    </div>
                                </div>
                            </div>
                        )}
                                                {sections.nav && (
                            <div className="preview-nav-card">
                                <div
                                    className="preview-nav"
                                    style={{
                                        background: navBackground,
                                    }}
                                >
                                    <div className="preview-nav-left">
                                        {content.nav?.logo ? (
                                            <img
                                                className="preview-logo-image"
                                                src={content.nav.logo}
                                                alt={content.nav?.brand || 'Logo'}
                                                style={logoStyles}
                                            />
                                        ) : (
                                            <div
                                                className="preview-logo"
                                                style={{ background: navAccent, color: '#ffffff' }}
                                            >
                                                Your Logo
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="preview-hamburger"
                                            aria-label="Toggle navigation menu"
                                            aria-expanded={navOpen}
                                            onClick={() => setNavOpen((prev) => !prev)}
                                        >
                                            ≡
                                        </button>
                                    </div>
                                    <div className={`preview-nav-menu ${navOpen ? 'open' : ''}`}>
                                        <div className="preview-menu">
                                            {(content.nav?.links || []).map((link, index) => {
                                                const linkColor = getNavLinkColor(index);
                                                const isActive = index === 0;
                                                return (
                                                    <span
                                                        key={link + index}
                                                        className={`preview-link ${isActive ? 'active' : ''}`}
                                                        style={{
                                                            borderColor: `${linkColor}55`,
                                                            background: isActive ? `${linkColor}33` : `${linkColor}1a`,
                                                            color: isActive ? '#fff' : '#e2e8f0',
                                                        }}
                                                    >
                                                        {link}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="preview-nav-actions">
                                        <span
                                            className="btn btn-primary btn-tiny"
                                            style={{
                                                background: navCtaColor,
                                                borderColor: navCtaColor,
                                            }}
                                        >
                                            {content.nav?.cta || 'Book now'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {sections.hero && (
                            <div
                                className="preview-hero-card"
                                style={{ padding: '28px', minHeight: '820px', display: 'grid', gap: '18px' }}
                            >
                                {heroBackgroundMediaType !== 'none' && heroBackgroundMediaUrl && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            zIndex: 0,
                                            overflow: 'hidden',
                                            borderRadius: 'inherit',
                                        }}
                                        aria-hidden="true"
                                    >
                                        {heroBackgroundMediaType === 'video' ? (
                                            <video
                                                src={heroBackgroundMediaUrl}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    filter: 'brightness(0.65)',
                                                }}
                                            />
                                        ) : (
                                            <img
                                                src={heroBackgroundMediaUrl}
                                                alt=""
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    filter: 'brightness(0.65)',
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                                <div className="hero-head" style={{ gap: '14px' }}>
                                    <div>
                                        <p className="preview-eyebrow">Hero</p>
                                        <h3 style={{ fontSize: '2.4rem', marginBottom: '6px' }}>{content.hero.headline}</h3>
                                        <p className="preview-lead" style={{ fontSize: '1.05rem', lineHeight: 1.6 }}>{content.hero.subhead}</p>
                                    </div>
                                    <div className="hero-chip-stack">
                                        <span className="chip hero-chip" style={{ borderColor: content.hero.accent, color: content.hero.accent }}>
                                            Premium fleet
                                        </span>
                                        <span className="chip ghost">4.9 ★ Rated</span>
                                    </div>
                                </div>

                                <div className="hero-form-shell" style={{ padding: '18px' }}>
                                    <div className="hero-form-header" style={{ marginBottom: '12px' }}>
                                        <div>
                                            <p className="card-eyebrow">Trip details</p>
                                            <p className="helper-text" style={{ fontSize: '0.98rem' }}>Enter dates and locations to see live availability.</p>
                                        </div>
                                        <span className="chip ghost">Instant pickup</span>
                                    </div>

                                    <div className="hero-form">
                                        <div className="hero-form-grid" style={{ gap: '12px' }}>
                                            <div className="hero-field">
                                                <label>Pick-up Date</label>
                                                <input type="text" value="12/11/2025" readOnly />
                                            </div>
                                            <div className="hero-field">
                                                <label>Pick-up Time</label>
                                                <input type="text" value="10:00 AM" readOnly />
                                            </div>
                                            <div className="hero-field">
                                                <label>Return Date</label>
                                                <input type="text" value="--/--/----" readOnly />
                                            </div>
                                            <div className="hero-field">
                                                <label>Return Time</label>
                                                <input type="text" value="10:00 AM" readOnly />
                                            </div>
                                        </div>

                                        <div className="hero-locations-grid" style={{ gap: '12px' }}>
                                            <div className="hero-field">
                                                <label>Pick-up location</label>
                                                <div className="hero-location">
                                                    <input type="text" value="Hogarth Road, London" readOnly />
                                                </div>
                                            </div>

                                            <div className="hero-field">
                                                <label>Return location</label>
                                                <div className="hero-location">
                                                    <input type="text" value="Hogarth Road, London" readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="preview-actions hero-actions-row" style={{ gap: '12px' }}>
                                        <span
                                            className="btn btn-primary btn-small"
                                            style={{
                                                background: heroPrimaryCtaColor,
                                                borderColor: heroPrimaryCtaColor,
                                                padding: '10px 18px',
                                                fontSize: '0.98rem',
                                                borderRadius: '9999px',
                                            }}
                                        >
                                            {content.hero.ctaPrimary}
                                        </span>
                                        <span
                                            className="btn btn-outline btn-small"
                                            style={{
                                                borderColor: heroSecondaryCtaColor,
                                                color: heroSecondaryCtaColor,
                                                padding: '10px 18px',
                                                fontSize: '0.98rem',
                                            }}
                                        >
                                            {content.hero.ctaSecondary}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {sections.icons && (
                            <div
                                className="icons-block"
                                style={{
                                    padding: '20px 18px',
                                    gap: '14px',
                                    background: iconsBackground,
                                }}
                            >
                                <div className="icons-block-head">
                                    <p className="section-tag" style={{ color: iconsTextColor }}>Icon Section</p>
                                </div>
                                <div className="icon-grid" style={{ gap: '14px' }}>
                                    {content.icons.items.map((item, index) => {
                                        const itemColor = getIconColor(index);
                                        return (
                                            <div key={index} className="icon-item">
                                                <div
                                                    className="icon-figure"
                                                    style={{
                                                        background: `${itemColor}26`,
                                                        borderColor: `${itemColor}50`,
                                                        color: itemColor,
                                                        width: `${iconSize}px`,
                                                        height: `${iconSize}px`,
                                                        fontSize: `${iconFontSize}px`,
                                                    }}
                                                >
                                                    {item.icon || index + 1}
                                                </div>
                                                <p className="icon-title" style={{ fontSize: '1.05rem', color: iconsTextColor }}>
                                                    {item.title}
                                                </p>
                                                <p className="icon-copy" style={{ fontSize: '0.98rem', color: iconsTextColor }}>
                                                    {item.description}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="icon-section-footer">
                                    <p className="icon-section-title" style={{ fontSize: '1.2rem', color: iconsTextColor }}>
                                        {content.icons.title}
                                    </p>
                                    <span
                                        className="btn btn-primary btn-small"
                                        style={{
                                            background: iconCtaColor,
                                            borderColor: iconCtaColor,
                                            padding: '9px 16px',
                                            fontSize: '0.95rem',
                                        }}
                                    >
                                        {content.icons.ctaLabel}
                                    </span>
                                </div>
                            </div>
                        )}

                        {sections.vehicles && (
                            <div className="preview-card wide">
                                <div
                                    style={{
                                        display: 'grid',
                                        gap: '22px',
                                        padding: '28px',
                                        borderRadius: '18px',
                                        background: vehiclesBackground,
                                        border: `1px solid ${content.vehicles.accent}2e`,
                                        boxShadow: '0 12px 35px rgba(0, 0, 0, 0.35)',
                                        color: vehiclesTextColor,
                                        width: '100%',
                                        maxWidth: previewMode === 'mobile' ? '100%' : '1240px',
                                        margin: '0 auto',
                                    }}
                                >
                                    <div style={{ display: 'grid', gap: '10px', textAlign: 'center' }}>
                                        <h3 style={{ margin: 0, fontSize: '2.6rem', color: vehiclesTextColor }}>
                                            {content.vehicles.heading || 'Find Your Drive'}
                                        </h3>
                                        <p
                                            className="helper-text"
                                            style={{
                                                margin: 0,
                                                maxWidth: '880px',
                                                justifySelf: 'center',
                                                fontSize: '1rem',
                                                color: vehiclesTextColor,
                                            }}
                                        >
                                            {content.vehicles.subhead || ''}
                                        </p>
                                    </div>

                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: previewMode === 'mobile' ? 'repeat(1, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
                                            gap: '16px',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {content.vehicles.items
                                            .filter((item) => item.showOnHomepage !== false)
                                            .map((item, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    position: 'relative',
                                                    background: item.backgroundColor || vehiclesCardBackgroundColor,
                                                    border: `1px solid ${content.vehicles.accent}24`,
                                                    borderRadius: '14px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
                                                    display: 'grid',
                                                    gridTemplateRows: '200px auto auto',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        position: 'relative',
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        backgroundImage: item.image
                                                            ? `url(${item.image})`
                                                            : `linear-gradient(135deg, ${content.vehicles.accent}26, ${content.vehicles.accent}12)`,
                                                    }}
                                                    role="img"
                                                    aria-label={item.title}
                                                >
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            background:
                                                                'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.55) 100%)',
                                                        }}
                                                    ></div>
                                                </div>
                                                <div style={{ padding: '16px 16px 10px', display: 'grid', gap: '8px' }}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: '8px',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <strong style={{ fontSize: '1.05rem', color: vehiclesTextColor }}>{item.title}</strong>
                                                        {formatVehiclePrice(item.price, item.rate) && (
                                                            <span
                                                                style={{
                                                                    color: content.vehicles.accent,
                                                                    fontWeight: 700,
                                                                    display: 'flex',
                                                                    alignItems: 'baseline',
                                                                    gap: '4px',
                                                                }}
                                                            >
                                                                <span style={{ fontSize: '1.25rem' }}>
                                                                    {formatVehiclePrice(item.price, item.rate)}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {item.description && (
                                                        <p style={{ margin: 0, color: vehiclesTextColor, fontSize: '0.98rem' }}>
                                                            {item.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '8px',
                                                        padding: '0 14px 12px',
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-small"
                                                        style={{
                                                            background: content.vehicles.accent,
                                                            borderColor: content.vehicles.accent,
                                                            borderRadius: '9999px',
                                                            padding: '9px 16px',
                                                            fontSize: '0.95rem',
                                                        }}
                                                    >
                                                        Rent Me
                                                    </button>
                                                    <span style={{ color: content.vehicles.accent, fontSize: '0.95rem' }}>
                                                        {item.note || content.vehicles.badge}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {sections.testimonials && (
                            <div
                                className="preview-card wide"
                                style={{
                                    background: testimonialsBackground,
                                    border: `1px solid ${content.testimonials.accent}33`,
                                    padding: '64px 22px',
                                    minHeight: '720px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    color: testimonialsTextColor,
                                }}
                            >
                                <div style={{ display: 'grid', gap: '18px' }}>
                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                            <span
                                                className="chip"
                                                style={{
                                                    borderColor: content.testimonials.accent,
                                                    color: content.testimonials.accent,
                                                }}
                                            >
                                                {content.testimonials.badge}
                                            </span>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    color: content.testimonials.accent,
                                                }}
                                            >
                                                {[...Array(5)].map((_, starIndex) => (
                                                    <span key={starIndex} aria-hidden="true">
                                                        ★
                                                    </span>
                                                ))}
                                                <span className="helper-text" style={{ color: testimonialsTextColor }}>
                                                    5.0 average rating
                                                </span>
                                            </div>
                                        </div>
                                        <h3 style={{ margin: 0, fontSize: '1.9rem', color: testimonialsTextColor }}>
                                            Thousands of Happy Customers
                                        </h3>
                                        <p className="helper-text" style={{ margin: 0, color: testimonialsTextColor }}>
                                            {content.testimonials.quote}
                                        </p>
                                    </div>

                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns:
                                                previewMode === 'mobile' ? 'repeat(1, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
                                            gap: '14px',
                                        }}
                                    >
                                        {testimonials.map((item, index) => (
                                            <div
                                                key={item.name + index}
                                                style={{
                                                    background: 'rgba(255,255,255,0.02)',
                                                    border: `1px solid ${content.testimonials.accent}26`,
                                                    borderRadius: '14px',
                                                    padding: '14px 14px 16px',
                                                    display: 'grid',
                                                    gap: '10px',
                                                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div
                                                        style={{
                                                            width: '52px',
                                                            height: '52px',
                                                            borderRadius: '50%',
                                                            overflow: 'hidden',
                                                            border: `2px solid ${content.testimonials.accent}`,
                                                            background: `${content.testimonials.accent}12`,
                                                        }}
                                                        aria-hidden="true"
                                                    >
                                                        <img
                                                            src={item.image}
                                                            alt={item.name}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </div>
                                                    <div style={{ display: 'grid', gap: '4px' }}>
                                                        <strong style={{ color: testimonialsTextColor }}>{item.name}</strong>
                                                        <span className="helper-text" style={{ color: testimonialsTextColor }}>
                                                            {item.title}
                                                        </span>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                color: content.testimonials.accent,
                                                            }}
                                                            aria-label="Rated 5 out of 5"
                                                        >
                                                            {[...Array(5)].map((_, starIndex) => (
                                                                <span key={starIndex} aria-hidden="true">
                                                                    ★
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <p style={{ margin: 0, color: testimonialsTextColor, lineHeight: 1.55 }}>
                                                    {item.quote}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {sections.why && (
                            <div
                                className="preview-card wide why-section"
                                style={{
                                    background: whyBackground,
                                    color: whyTextColor,
                                    padding: '72px 0',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'grid',
                                        gap: '14px',
                                        padding: '18px',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                        <div style={{ display: 'grid', gap: '4px' }}>
                                            <p className="card-eyebrow" style={{ margin: 0 }}>Why Rent With Us</p>
                                            <h3 style={{ margin: 0, color: whyTextColor }}>Built for fast, reliable rentals</h3>
                                            <p className="helper-text" style={{ margin: 0, color: whyTextColor }}>
                                                Instant pickups, verified insurance, and transparent pricing with no surprises.
                                            </p>
                                        </div>
                                        <span
                                            className="chip"
                                            style={{
                                                borderColor: content.why.accent,
                                                color: content.why.accent,
                                            }}
                                        >
                                            4.9 ★ rated
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: previewMode === 'mobile' ? 'repeat(1, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
                                            gap: '14px',
                                        }}
                                    >
                                        {whyItems.map((item, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    background: 'rgba(255,255,255,0.02)',
                                                    border: `1px solid ${content.why.accent}24`,
                                                    borderRadius: '12px',
                                                    padding: '14px',
                                                    display: 'grid',
                                                    gap: '10px',
                                                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.16)',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '44px',
                                                        height: '44px',
                                                        borderRadius: '12px',
                                                        background: `${content.why.accent}22`,
                                                        border: `1px solid ${content.why.accent}55`,
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        fontSize: '1.2rem',
                                                    }}
                                                    aria-hidden="true"
                                                >
                                                    {item.icon || '✓'}
                                                </div>
                                                <div style={{ display: 'grid', gap: '6px' }}>
                                                    <strong style={{ color: whyTextColor }}>{item.title}</strong>
                                                    <p style={{ margin: 0, color: whyTextColor, lineHeight: 1.5 }}>{item.description}</p>
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        fontSize: '0.92rem',
                                                        color: whyTextColor,
                                                    }}
                                                >
                                                    <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: content.why.accent }}></span>
                                                    <span>Instant booking ready</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {sections.photos && (
                            <div
                                className="preview-card wide"
                                style={{
                                    background: photosBackground,
                                    padding: '24px',
                                }}
                            >
                                <p className="card-eyebrow">Photos</p>
                                <div className="photo-grid">
                                    {photoItems.map((item, index) => (
                                        <div
                                            key={index}
                                            className="photo-item"
                                            style={{
                                                borderColor: `${content.photos.accent}40`,
                                                background: item.image
                                                    ? `linear-gradient(180deg, rgba(15, 23, 42, 0.2), rgba(15, 23, 42, 0.75)), url(${item.image})`
                                                    : `linear-gradient(135deg, ${content.photos.accent}33, ${content.photos.accent}18)`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                            }}
                                        >
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {sections.email && (
                            <div
                                className="preview-card wide"
                                style={{
                                    background: emailBackground,
                                    color: emailTextColor,
                                    padding: '24px',
                                }}
                            >
                                <div className="email-intro">
                                    <h3>Want 10% off your first rental with us?</h3>
                                    <p className="helper-text">Subscribe to our email list & receive 10% off today</p>
                                </div>
                                <div className="email-row">
                                    <input type="email" placeholder={content.email.placeholder} />
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-small"
                                        style={{
                                            background: content.email.accent,
                                            borderColor: content.email.accent,
                                        }}
                                    >
                                        {content.email.buttonText}
                                    </button>
                                </div>
                            </div>
                        )}

                        {sections.footer && (
                            <div
                                className="preview-card wide footer-section"
                                style={{
                                    background: footerBackground,
                                    color: footerTextColor,
                                    padding: '32px 22px',
                                }}
                            >
                                <div className="footer-grid">
                                    <div className="footer-brand">
                                        {content.nav?.logo && (
                                            <img
                                                className="footer-logo"
                                                src={content.nav.logo}
                                                alt={content.nav?.brand || 'Logo'}
                                                style={logoStyles}
                                            />
                                        )}
                                        {!content.nav?.logo && (
                                            <strong className="footer-title">{content.footer.title}</strong>
                                        )}
                                        <p className="helper-text">{content.footer.tagline}</p>
                                        <div className="footer-badge">{content.footer.badgeText}</div>
                                    </div>
                                    <div className="footer-links">
                                        <div className="footer-column">
                                            <p className="footer-heading">Links</p>
                                            <div className="footer-link-row">
                                                <a className="footer-link" href="/privacy-policy">Privacy Policy</a>
                                                <a className="footer-link" href="/terms-of-service">Terms of Service</a>
                                                <a className="footer-link" href="/car-rental-agreement">Car Rental Agreement</a>
                                            </div>
                                            <p className="footer-heading footer-heading-spaced">{content.footer.contactTitle}</p>
                                            <a className="footer-link" href={`mailto:${content.footer.contactEmail}`}>
                                                {content.footer.contactEmail}
                                            </a>
                                            <span className="footer-link">{content.footer.contactPhone}</span>
                                        </div>
                                        <div className="footer-column">
                                            <p className="footer-heading">{content.footer.hoursTitle}</p>
                                            <span className="footer-link">{content.footer.hoursDays}</span>
                                            <span className="footer-link">{content.footer.hoursTime}</span>
                                            <p className="footer-heading footer-heading-spaced">{content.footer.followTitle}</p>
                                            <div className="footer-social">
                                                <a
                                                    className="footer-link footer-social-link"
                                                    href={content.footer.instagramUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <span className="footer-social-icon" aria-hidden="true">
                                                        <svg viewBox="0 0 24 24" role="presentation">
                                                            <path
                                                                d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm10 2H7a2 2 0 0 0-2 2v10c0 1.1.9 2 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm-5 3.2A3.8 3.8 0 1 1 8.2 12 3.81 3.81 0 0 1 12 8.2zm0 2A1.8 1.8 0 1 0 13.8 12 1.8 1.8 0 0 0 12 10.2zm4.3-2.35a1 1 0 1 1-1-1 1 1 0 0 1 1 1z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                    </span>
                                                    {content.footer.instagramLabel}
                                                </a>
                                                <a
                                                    className="footer-link footer-social-link"
                                                    href={content.footer.facebookUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <span className="footer-social-icon" aria-hidden="true">
                                                        <svg viewBox="0 0 24 24" role="presentation">
                                                            <path
                                                                d="M13.5 8.5V7.2c0-.7.4-1.1 1.2-1.1h1.3V4h-2.2C11.6 4 10.5 5.2 10.5 7.1v1.4H9v2.7h1.5V20h3V11.2h2.2l.4-2.7h-2.6z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                    </span>
                                                    {content.footer.facebookLabel}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="footer-bottom">
                                    <span className="footer-note">© 2025 {content.footer.title}. All rights reserved.</span>
                                    <div className="footer-legal">
                                        <span className="footer-link">Privacy</span>
                                        <span className="footer-link">Terms</span>
                                        <span className="footer-link">Security</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

export default LiveDemo;
